import { baseApi } from '@/store/api/baseApi';
import type {
  BulkModerateReviewsPayload,
  BulkReviewIdsPayload,
  ModerateReviewPayload,
  PaginatedReviews,
  ProductReviewsResult,
  Review,
  ReviewListParams,
} from '@/types/review';

function buildReviewListQuery(params: ReviewListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'createdAt');
  searchParams.set('sortOrder', params.sortOrder ?? 'desc');

  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.status) searchParams.set('status', params.status);
  if (params.rating) searchParams.set('rating', String(params.rating));
  if (params.productId) searchParams.set('productId', params.productId);
  if (params.customerId) searchParams.set('customerId', params.customerId);

  return searchParams.toString();
}

function productReviewTag(productId: string) {
  return { type: 'Reviews' as const, id: `PRODUCT_${productId}` };
}

export const reviewsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getReviews: builder.query<PaginatedReviews, ReviewListParams>({
      query: (params) => ({
        url: `/reviews?${buildReviewListQuery(params)}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildReviewListQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildReviewListQuery(currentArg ?? {}) !== buildReviewListQuery(previousArg ?? {}),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Reviews' as const, id })),
              { type: 'Reviews', id: 'LIST' },
            ]
          : [{ type: 'Reviews', id: 'LIST' }],
    }),
    getProductReviews: builder.query<ProductReviewsResult, string>({
      query: (productId) => `/reviews/product/${productId}`,
      providesTags: (_result, _error, productId) => [
        productReviewTag(productId),
        { type: 'Reviews', id: 'PRODUCT_LIST' },
      ],
    }),
    moderateReview: builder.mutation<Review, { id: string; body: ModerateReviewPayload }>({
      query: ({ id, body }) => ({ url: `/reviews/moderate/${id}`, method: 'POST', body }),
      invalidatesTags: (result, _error, { id }) => [
        { type: 'Reviews', id },
        { type: 'Reviews', id: 'LIST' },
        { type: 'Reviews', id: 'PRODUCT_LIST' },
        ...(result?.productId ? [productReviewTag(result.productId)] : []),
      ],
    }),
    bulkModerateReviews: builder.mutation<{ count: number }, BulkModerateReviewsPayload>({
      query: (body) => ({ url: '/reviews/bulk-moderate', method: 'POST', body }),
      invalidatesTags: [
        { type: 'Reviews', id: 'LIST' },
        { type: 'Reviews', id: 'PRODUCT_LIST' },
      ],
    }),
    deleteReview: builder.mutation<void, string>({
      query: (id) => ({ url: `/reviews/delete/${id}`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Reviews', id },
        { type: 'Reviews', id: 'LIST' },
        { type: 'Reviews', id: 'PRODUCT_LIST' },
      ],
    }),
    bulkDeleteReviews: builder.mutation<{ count: number }, BulkReviewIdsPayload>({
      query: (body) => ({ url: '/reviews/bulk-delete', method: 'POST', body }),
      invalidatesTags: [
        { type: 'Reviews', id: 'LIST' },
        { type: 'Reviews', id: 'PRODUCT_LIST' },
      ],
    }),
  }),
});

export const {
  useGetReviewsQuery,
  useGetProductReviewsQuery,
  useModerateReviewMutation,
  useBulkModerateReviewsMutation,
  useDeleteReviewMutation,
  useBulkDeleteReviewsMutation,
} = reviewsApi;
